import React, { useEffect, useRef, useState } from "react";
import { ThumbsUp, ThumbsDown, Flag, ChartLine, X } from "lucide-react";
import { useTheme } from "../ThemeContext.tsx";
import { motion } from "framer-motion";

import apiService from "../services/api.ts";

const isValidGuid = (id?: string) =>
    !!id &&
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
        id
    );

type MessageFeedbackProps = {
    message: any;
    sessionId: string;
    interactionId?: string;
    onFeedbackChange: (fb: any) => void;
    // NEW: parent will pass this
    onChartClick?: () => void;
};

const MessageFeedback: React.FC<MessageFeedbackProps> = ({
    message,
    sessionId,
    interactionId,
    onFeedbackChange,
    onChartClick,
}) => {
    const { colors, isMobile } = useTheme();
    const [flagModalOpen, setFlagModalOpen] = useState(false);
    const [flagReason, setFlagReason] = useState("");
    const [showThankYou, setShowThankYou] = useState(false);
    const thankYouTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (thankYouTimeout.current) {
                clearTimeout(thankYouTimeout.current);
            }
        };
    }, []);

    // Reset thank you state when modal closes to ensure clean state
    useEffect(() => {
        if (!flagModalOpen) {
            setShowThankYou(false);
        }
    }, [flagModalOpen]);

    const handleLike = () => {
        const newFeedback = {
            ...message.feedback,
            isLiked: !message.feedback?.isLiked,
            isDisliked: false,
        };

        onFeedbackChange(newFeedback);
        console.log("Like toggled for message", message.id, newFeedback.isLiked);

        if (!interactionId || !isValidGuid(interactionId)) return;

        apiService
            .likeInteraction(sessionId, interactionId, !!newFeedback.isLiked)
            .then(() => {
                console.log("Like saved:", interactionId);
            })
            .catch((e) => {
                console.error("Failed to update like:", e);
                // 3️⃣ Optionally revert UI if call failed
                onFeedbackChange(message.feedback || {});
            });
    };

    const handleDislike = () => {
        const newFeedback = {
            ...message.feedback,
            isDisliked: !message.feedback?.isDisliked,
            isLiked: false,
        };

        onFeedbackChange(newFeedback);
        console.log(
            "Dislike toggled for message",
            message.id,
            newFeedback.isDisliked
        );

        if (!interactionId || !isValidGuid(interactionId)) return;
        apiService
            .likeInteraction(sessionId, interactionId, false)
            .then(() => {
                console.log("Dislike saved:", interactionId);
            })
            .catch((e) => {
                console.error("Failed to update dislike:", e);
                onFeedbackChange(message.feedback || {});
            });
    };

    const handleCloseModal = () => {
        setFlagModalOpen(false);
        setShowThankYou(false);
        if (thankYouTimeout.current) {
            clearTimeout(thankYouTimeout.current);
        }
    };

    const handleFlag = () => {
        if (showThankYou) {
            setShowThankYou(false);
            if (thankYouTimeout.current) {
                clearTimeout(thankYouTimeout.current);
            }
        }
        if (message.feedback?.isFlagged) {
            onFeedbackChange({
                ...message.feedback,
                isFlagged: false,
                flagReason: undefined,
            });
            console.log("Unflagged message", message.id);
        } else {
            setShowThankYou(false);
            setFlagModalOpen(true);
        }
    };

    const handleFlagSubmit = async () => {
        if (!flagReason.trim()) return;

        const reasonText = flagReason.trim();
        setFlagReason("");
        setShowThankYou(true);

        onFeedbackChange({
            ...message.feedback,
            isFlagged: true,
            flagReason: reasonText,
        });
        console.log("Thank you for your feedback!");
        console.log("Flagged message", message.id, "with reason:", reasonText);

        if (!interactionId || !isValidGuid(interactionId)) {
            // If no valid interactionId, just close after timeout
            if (thankYouTimeout.current) {
                clearTimeout(thankYouTimeout.current);
            }
            thankYouTimeout.current = setTimeout(() => {
                handleCloseModal();
            }, 1000);
            return;
        }

        apiService
            .reportInteraction(sessionId, interactionId, reasonText)
            .then(() => {
                console.log("Flag saved", interactionId);
                // Close modal after 1 second - modal will disappear completely
                if (thankYouTimeout.current) {
                    clearTimeout(thankYouTimeout.current);
                }
                thankYouTimeout.current = setTimeout(() => {
                    handleCloseModal();
                }, 1000);
            })
            .catch((e) => {
                console.error("Failed to report:", e);
                setShowThankYou(false);
                onFeedbackChange({
                    ...message.feedback,
                    isFlagged: false,
                    flagReason: undefined,
                });
            });
    };

    const handleChart = () => {
        if (!onChartClick) {
            console.warn("onChartClick is not provided");
            return;
        }
        onChartClick(); // parent will send "Create chart"
    };

    const baseButton = isMobile
        ? "flex items-center justify-center p-2 rounded-full text-xs font-medium transition-all border shadow-sm"
        : "flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all border shadow-sm";

    return (
        <>
            <div
                className={`flex items-center gap-3 mt-3 px-2 py-2 rounded-xl border`}
            >
                {/* LIKE */}
                <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={handleLike}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1 }}
                    className={`${baseButton} ${message.feedback?.isLiked
                            ? "bg-green-100 text-gray-600 border-transparent"
                            : "hover:bg-green-100 text-gray-600 border-transparent"
                        }`}
                    title="Like this response"
                >
                    <ThumbsUp
                        size={16}
                        className="text-green-600"
                    />
                    {!isMobile && <span>Like</span>}
                </motion.button>

                {/* DISLIKE */}
                <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={handleDislike}
                    className={`${baseButton} ${message.feedback?.isDisliked
                            ? "bg-red-100 text-gray-600 border-transparent"
                            : "hover:bg-red-100 text-gray-600 border-transparent"
                        }`}
                    title="Dislike this response"
                >
                    <ThumbsDown
                        size={16}
                        className="text-red-600"
                    />
                    {!isMobile && <span>Dislike</span>}
                </motion.button>

                {/* FLAG */}
                <motion.button
                    // whileTap={{ scale: 0.9 }}
                    onClick={handleFlag}
                    className={`${baseButton} ${message.feedback?.isFlagged
                            ? "bg-orange-100 text-gray-600 border-transparent"
                            : "hover:bg-orange-100 text-gray-600 border-transparent"
                        }`}
                    title="Flag this response"
                >
                    <Flag
                        size={16}
                        className="text-orange-600"
                    />
                    {!isMobile && <span>Feedback</span>}
                </motion.button>

                {/* CHART */}
                <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={handleChart}
                    className={`${baseButton} hover:bg-blue-100 text-gray-600 border-transparent`}
                    title="Generate chart for this response"
                >
                    <ChartLine size={16} className="text-blue-600" />
                    {!isMobile && <span>Chart</span>}
                </motion.button>
            </div>

            {/* Flag Modal */}
            {flagModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center"
                    onClick={handleCloseModal}
                >
                    <div className="absolute inset-0 bg-black/50" />
                    <div
                        className={`relative w-full max-w-md mx-4 rounded-lg border ${colors.border.primary} ${colors.bg.secondary} p-4`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button (X) */}
                        <button
                            onClick={handleCloseModal}
                            className={`absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${colors.text.secondary}`}
                            aria-label="Close modal"
                        >
                            <X size={20} />
                        </button>

                        {showThankYou ? (
                            <p className={`${colors.text.secondary} text-center font-bold my-2`}>Thank you for your feedback!</p>
                        ) : (
                            <>
                                <h3 className={`text-lg font-semibold mb-3 ${colors.text.primary}`}>
                                    Feedback
                                </h3>
                                <textarea
                                    value={flagReason}
                                    onChange={(e) => setFlagReason(e.target.value)}
                                    placeholder="Please provide more details..."
                                    className={`w-full ${colors.bg.tertiary} border ${colors.border.secondary} rounded-md p-3 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm ${colors.text.primary}`}
                                    rows={4}
                                />
                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        onClick={handleCloseModal}
                                        className={`px-3 py-1 rounded-md text-sm hover:text-black hover:bg-${colors.interactive.primaryHover}`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleFlagSubmit}
                                        disabled={!flagReason.trim()}
                                        className={`px-3 py-1 rounded-md text-sm text-white ${colors.interactive.primary} ${colors.interactive.primaryHover} disabled:opacity-50`}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default MessageFeedback;
